/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import { Button } from '@scuf/common';
import descriptors from '../../../../locales/en-US/descriptors';
import { ApiContext } from '../../../../context/ApiContext';
import {
  euiDefaultUnit,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import { EnergyUsageField } from '../../../shared/Enums';
import { IEmaasEnergyUsageComponentData } from '../../IEnergyManagementProps';
import ProgressBarWidget from '../../../shared/ProgressBar/ProgressBarWidget';
import BreakdownByMeter from '../../../shared/BreakdownByMeter/BreakdownByMeter';
import {
  basicGraphEnergy,
  convertDataForBreakdownByMeter,
  convertDataForBreakdownByMeterWithIndividualScaling,
  getSpanDetails
} from '../../../shared/Utils/Utils';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import ChartWidget from '../../../shared/Chart/ChartWidget';
import {
  convertDataforPortfolioBreakdownByMeter,
  convertDataforPortfolioEnergyBreakdownBySite,
  getDateLabel,
  getNullToZero,
  headersEnergyUsageBasic
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { IMeterDataProps } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import { getEndDateForBasicSubscription } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { getEuiUnitDisplayName } from '../../../shared/Utils/unitHandling/EuiUnitHandling';
import { IGrapghData } from '../../../shared/Chart/IChartWidgetProps';
interface IEmaasBasicSubscriptionEnergyUsageComponentProps {
  data: IEmaasEnergyUsageComponentData;
  viewType?: string;
}

export const EmaasBasicSubscriptionEnergyUsageComponentSite = (
  props: IEmaasBasicSubscriptionEnergyUsageComponentProps
) => {
  const [t, lng] = useTranslation();
  const d = descriptors.energyManagement;
  const { data, viewType } = props;
  const apiContext = useContext(ApiContext);
  const {
    redirect,
    preferredEuiUnit,
    onEnergyUsageViewDetailsClick,
    userPreferedUnit
  } = apiContext;
  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;
  const {
    summary: {
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage
    },
    breakdownByMeter,
    breakdownBySite,
    aggregationsStartDate,
    aggregationsEndDate,
    performanceAggregationApplied,
    monthWiseAggregations,
    energyMeasurementUnit,
    rootNodeId,
    totalSiteCount,
    euiEnergyMeasuringUnit
  } = data;

  const onViewDetailsClick = () => {
    if (
      viewType === EnergyUsageField.SITEENERGY &&
      onEnergyUsageViewDetailsClick
    ) {
      onEnergyUsageViewDetailsClick();
    }
    if (redirect) {
      redirect(`/energyViewDetails/root/${rootNodeId}/portfolio-view`);
    }
  };

  const {
    startMonth,
    startYear,
    endMonth,
    endYear,
    startDate,
    endDate
  } = getSpanDetails(aggregationsStartDate, aggregationsEndDate);

  let consumptionBreakdownMeterData;

  if (viewType == EnergyUsageField.SITEENERGY) {
    consumptionBreakdownMeterData = convertDataForBreakdownByMeterWithIndividualScaling(
      breakdownByMeter,
      fromUnit,
      [],
      energyMeasurementUnit,
      apiContext.showChangeWhenData
    );
  } else if (viewType == EnergyUsageField.PORTFOLIOWATER.toString()) {
    consumptionBreakdownMeterData = convertDataforPortfolioEnergyBreakdownBySite(
      breakdownBySite,
      viewType == EnergyUsageField.PORTFOLIOWATER.toString()
        ? energyMeasurementUnit
        : fromUnit, //energyMeasurementUnit,
      '',
      '',
      [],
      energyMeasurementUnit,
      apiContext.showChangeWhenData,
      ConversionType.WATER
    );
  } else {
    consumptionBreakdownMeterData = convertDataforPortfolioEnergyBreakdownBySite(
      breakdownBySite,
      fromUnit, //energyMeasurementUnit,
      euiEnergyMeasuringUnit || euiDefaultUnit,
      preferredEuiUnit || '',
      [],
      energyMeasurementUnit,
      apiContext.showChangeWhenData
    );
  }

  const { values } = consumptionBreakdownMeterData;

  const [usageType, setUsageType] = useState<string>(t(d.HIGHWITHOUTUNIT));
  const onSortingHandler = (sortOrder: number): void => {
    if (viewType === EnergyUsageField.SITEENERGY) {
      sortOrder === 1
        ? setUsageType(t(d.LOWWITHNOUNIT))
        : setUsageType(t(d.HIGHWITHOUTUNIT));
    }
  };

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    viewType == EnergyUsageField.PORTFOLIOWATER.toString()
      ? ConversionType.WATER
      : ConversionType.ELECTRICITY,
    { previousPeriod: totalBaselineConsumption, actual: totalConsumption },
    totalEnergyDeviation,
    viewType == EnergyUsageField.PORTFOLIOWATER.toString()
      ? energyMeasurementUnit
      : fromUnit,
    undefined,
    null,
    null,
    energyMeasurementUnit,
    apiContext.showChangeWhenData,
    true
  );

  const graphData = basicGraphEnergy(
    monthWiseAggregations,
    viewType == EnergyUsageField.PORTFOLIOWATER.toString()
      ? energyMeasurementUnit
      : fromUnit,
    energyMeasurementUnit,
    viewType == EnergyUsageField.PORTFOLIOWATER.toString()
      ? ConversionType.WATER
      : ConversionType.ELECTRICITY,
    lng
  );

  const isPercentageGreater =
    Math.abs(
      Number(
        getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      )
    ) > PERCENT_CHANGE_THRESHOLD;

  const aggregationEnergyData = {
    actual: progressBarUnitScaledData.actual,
    previousPeriod: progressBarUnitScaledData.previousPeriod,
    savings:
      apiContext.showChangeWhenData &&
      progressBarUnitScaledData.deviation !== null &&
      isPercentageGreater
        ? null
        : progressBarUnitScaledData.deviation,
    actualUnit: progressBarUnitScaledData.actualUnit,
    baselineUnit: progressBarUnitScaledData.baselineUnit,
    changeUnit: progressBarUnitScaledData.changeUnit,
    savingsPercentage: parseFloat(
      getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      )
    )
  };

  const convertedEuiUnit = getEuiUnitDisplayName(preferredEuiUnit || '');
  return (
    <div className="kpi-card-flex-container">
      <div className="kpi-card-progress-bar-container">
        <ProgressBarWidget
          category="EnergyUsage"
          aggregationData={aggregationEnergyData}
          unit={progressBarUnitScaledData.ConvertedUnit}
          deviationScaledData={progressBarUnitScaledData.deviationScaledData}
          basicSub={'basicSub'}
          fromCarbonKPI={true}
          originalUnit={energyMeasurementUnit}
          conversionType={ConversionType.ELECTRICITY}
        />
      </div>
      <div className="kpi-card-monthwise-container">
        <div className="kpi-card-monthwise-date-range">
          {t(d.ENERGYUSAGEDETAILS)}{' '}
          {getDateLabel(
            startMonth,
            endMonth,
            performanceAggregationApplied,
            startYear,
            endYear,
            startDate,
            endDate
          )}
        </div>
        <ChartWidget
          graphData={graphData as IGrapghData}
          unit={graphData.chartUnit}
          axisUnitType="consumption"
        />
        <div className="kpi-card-view-details">
          <Button
            type="link"
            content={t(d.VIEWDETAILS)}
            className="view-details-button"
            onClick={() => onViewDetailsClick()}
          />
        </div>
      </div>
      <div className="kpi-card-meter-container">
        <BreakdownByMeter
          columnHeaders={headersEnergyUsageBasic(
            t,
            viewType,
            totalSiteCount,
            '',
            usageType,
            convertedEuiUnit
          )}
          meterData={(values as unknown) as IMeterDataProps[]}
          category={EnergyUsageField.ENERGYUSAGEBREAKDOWN}
          onSorting={onSortingHandler}
          energyMeasurementUnit={energyMeasurementUnit}
        />
      </div>
    </div>
  );
};
export default memo(EmaasBasicSubscriptionEnergyUsageComponentSite);
